import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BarChart柱状图组件
 * 提供简单易用的柱状图显示功能，无需外部依赖
 *
 * @example
 * ```tsx
 * // 基本用法
 * <BarChart
 *   data={[
 *     { label: '低档模型', value: 120, color: '#3b82f6' },
 *     { label: '中档模型', value: 80, color: '#10b981' },
 *     { label: '高档模型', value: 45, color: '#f59e0b' }
 *   ]}
 * />
 *
 * // 水平柱状图
 * <BarChart
 *   data={usageData}
 *   orientation="horizontal"
 *   showValues={true}
 * />
 *
 * // 带动画的柱状图
 * <BarChart
 *   data={salesData}
 *   animated={true}
 *   animationDuration={800}
 * />
 * ```
 */
import { forwardRef, useEffect, useState } from 'react';
import { cn } from '../../utils/cn';
/**
 * 默认值格式化函数
 */
const defaultValueFormatter = (value) => {
    return value.toLocaleString();
};
/**
 * BarChart柱状图组件
 */
export const BarChart = forwardRef(({ data, orientation = 'vertical', showValues = false, showGrid = true, animated = true, animationDuration = 600, barSpacing = 8, defaultColor = '#3b82f6', maxValue, valueFormatter = defaultValueFormatter, className, onBarClick, onBarHover, ...props }, ref) => {
    const [animatedValues, setAnimatedValues] = useState(animated ? data.map(() => 0) : data.map(item => item.value));
    // 计算最大值
    const calculatedMaxValue = maxValue || Math.max(...data.map(item => item.value));
    // 动画效果
    useEffect(() => {
        if (!animated)
            return;
        const timer = setTimeout(() => {
            setAnimatedValues(data.map(item => item.value));
        }, 100);
        return () => clearTimeout(timer);
    }, [data, animated]);
    // 获取柱子的百分比高度/宽度
    const getBarPercentage = (value) => {
        if (calculatedMaxValue === 0)
            return 0;
        return (value / calculatedMaxValue) * 100;
    };
    // 渲染垂直柱状图
    const renderVerticalBars = () => (_jsxs("div", { className: "flex flex-col h-full", children: [_jsx("div", { className: "flex-1 flex justify-between", children: data.map((item, index) => {
                    const percentage = getBarPercentage(animatedValues[index]);
                    const color = item.color || defaultColor;
                    return (_jsxs("div", { className: "flex flex-col justify-end items-center w-full", style: {
                            flex: 1,
                            marginLeft: index > 0 ? barSpacing / 2 : 0,
                            marginRight: index < data.length - 1 ? barSpacing / 2 : 0,
                        }, children: [showValues && (_jsx("div", { className: "text-xs text-gray-600 mb-1", children: valueFormatter(item.value) })), _jsx("div", { className: cn('w-full rounded-t-sm cursor-pointer transition-all duration-200', 'hover:opacity-80 hover:shadow-md'), style: {
                                    height: `${percentage}%`,
                                    backgroundColor: color,
                                    transition: animated ? `height ${animationDuration}ms ease-out` : 'none',
                                    minHeight: '2px',
                                }, onClick: () => onBarClick?.(item, index), onMouseEnter: () => onBarHover?.(item, index), onMouseLeave: () => onBarHover?.(null) })] }, `bar-${index}`));
                }) }), _jsx("div", { className: "flex justify-between pt-2", children: data.map((item, index) => (_jsx("div", { className: "text-xs text-gray-600 text-center break-words w-full", style: {
                        flex: 1,
                        marginLeft: index > 0 ? barSpacing / 2 : 0,
                        marginRight: index < data.length - 1 ? barSpacing / 2 : 0,
                    }, children: item.label }, `label-${index}`))) })] }));
    // 渲染水平柱状图
    const renderHorizontalBars = () => (_jsx("div", { className: "space-y-4", children: data.map((item, index) => {
            const percentage = getBarPercentage(animatedValues[index]);
            const color = item.color || defaultColor;
            return (_jsxs("div", { className: "flex items-center", children: [_jsx("div", { className: "w-20 text-sm text-gray-600 text-right pr-3 flex-shrink-0", children: item.label }), _jsxs("div", { className: "flex-1 relative", children: [_jsx("div", { className: cn('h-6 rounded cursor-pointer transition-all duration-200', 'hover:opacity-80 hover:shadow-md'), style: {
                                    width: `${percentage}%`,
                                    backgroundColor: color,
                                    transition: animated ? `width ${animationDuration}ms ease-out` : 'none',
                                    minWidth: '2px',
                                }, onClick: () => onBarClick?.(item, index), onMouseEnter: () => onBarHover?.(item, index), onMouseLeave: () => onBarHover?.(null) }), showValues && (_jsx("div", { className: "absolute top-1/2 transform -translate-y-1/2 text-xs", style: {
                                    left: percentage > 15 ? '8px' : `${percentage}% + 8px`,
                                    color: percentage > 15 ? 'white' : '#4b5563',
                                }, children: valueFormatter(item.value) }))] })] }, index));
        }) }));
    // 渲染网格线（仅垂直图表）
    const renderGrid = () => {
        if (!showGrid || orientation === 'horizontal')
            return null;
        const gridLines = [];
        for (let i = 0; i <= 4; i++) {
            const percentage = (i / 4) * 100;
            gridLines.push(_jsx("div", { className: "absolute w-full border-t border-gray-200", style: { bottom: `${percentage}%` } }, i));
        }
        return gridLines;
    };
    if (data.length === 0) {
        return (_jsx("div", { ref: ref, className: cn('flex items-center justify-center h-full', className), ...props, children: _jsx("p", { className: "text-gray-600 text-sm", children: "No data available" }) }));
    }
    return (_jsxs("div", { ref: ref, className: cn('bar-chart relative w-full h-full p-4', className), ...props, children: [renderGrid(), _jsx("div", { className: "relative h-full", children: orientation === 'vertical' ? renderVerticalBars() : renderHorizontalBars() })] }));
});
BarChart.displayName = 'BarChart';
export default BarChart;
//# sourceMappingURL=BarChart.js.map